import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  fetchAgentList,
  fetchAgentLogById,
  fetchAllMismatchRequests,
} from '../hooks/queries';
import { IRaiseMismatch } from '../api/queries';
import { useNotifState } from '../state/notifs';
import TableWithSearchCTA from '../components/TableWithSearchCTA';
import SideModal from '../components/common/SideModal';
import MismatchStatus from '../components/MismatchStatus';
import MismatchRequest from '../components/MismatchRequest';
import { useMismatchCol } from '../components/columns/useMismatchCol';

const MismatchRequests = () => {
  const queryClient = useQueryClient();
  const { setShowNotification } = useNotifState();

  const [selectedMismatchRequest, setSelectedMismatchRequest] =
    useState<IRaiseMismatch>();

  const {
    data: mismatchRequests,
    isLoading: isLoadingMismatchRequests,
    isFetching: isFetchingMismatchRequests,
    refetch: refetchMismatchRequests,
    error,
  } = fetchAllMismatchRequests();

  const {
    data: agentLog,
    isLoading: isLoadingAgentLog,
    refetch: refetchAgentLog,
  } = fetchAgentLogById({
    id: selectedMismatchRequest?.logId!,
    enabled: !!selectedMismatchRequest,
    retry: false,
  });

  const {
    data: agents,
    isLoading: isLoadingAgents,
    isFetching: isFetchingAgents,
  } = fetchAgentList();

  const columns = useMismatchCol({
    onView: (data) => setSelectedMismatchRequest(data),
    agents: agents?.agents ?? [],
  });

  const closeMismatchModal = () => setSelectedMismatchRequest(undefined);

  const onMismatchRequestEdit = () => {
    queryClient.invalidateQueries({
      queryKey: ['mismatchRequests'] as any,
    });

    Promise.all([
      refetchAgentLog().catch(() => toast.error('Error refetching agent log')),
      refetchMismatchRequests().catch(() =>
        toast.error('Error refetching mismatch requests')
      ),
    ]).finally(closeMismatchModal);
  };

  useEffect(() => {
    setShowNotification(false);
  }, []);

  useEffect(() => {
    if (selectedMismatchRequest && !isLoadingAgentLog && !agentLog) {
      toast.error('Log not found');
      setSelectedMismatchRequest(undefined);
    }
  }, [selectedMismatchRequest, isLoadingAgentLog, agentLog]);

  return (
    <div
      className="w-full flex flex-col"
      style={{ height: 'calc(100vh - 3rem)' }}
    >
      <TableWithSearchCTA
        columnDefs={columns}
        rowData={mismatchRequests?.data ?? []}
        isLoading={isLoadingMismatchRequests || isLoadingAgents}
        isFetching={isFetchingMismatchRequests || isFetchingAgents}
        error={error as Error}
        refetch={refetchMismatchRequests}
        headerTitle="Mismatch Requests"
        enableRefresh
        storageKey="mismatch_requests_table"
      />
      <SideModal
        title={
          <h2 className="text-lg font-semibold flex items-center">
            Mismatch Resolution Request
            <span className="ml-2">
              <MismatchStatus status={selectedMismatchRequest?.status} />
            </span>
          </h2>
        }
        isOpen={!!selectedMismatchRequest && !!agentLog && !isLoadingAgentLog}
        onClose={closeMismatchModal}
      >
        <MismatchRequest
          mismatchRequest={selectedMismatchRequest}
          agentLogForMismatch={agentLog?.data}
          onEdit={() => {
            toast.success('Mismatch request resolved successfully!');
            onMismatchRequestEdit();
          }}
          onEditError={() => toast.error('Error updating mismatch request')}
          onStatusUpdate={(status) => {
            toast.success(`Mismatch request ${status}!`);
            onMismatchRequestEdit();
          }}
          onStatusUpdateError={() =>
            toast.error('Error updating mismatch request status')
          }
        />
      </SideModal>
    </div>
  );
};

export default MismatchRequests;
